import React, { memo } from 'react';

import { Theme } from '../../types/Theme';
import { Box } from '../Box/Box';
import { Text } from '../Text/Text';

interface Props {
  /** Adjust the flex property in flex layouts */
  flex?: 'auto' | 'none';
  /** Direction of divider */
  isHorizontal?: boolean;
  /** Optional display label for the divider */
  label?: string;
  /** Supported heights */
  height?: 's';
  /** Supported widths */
  width?: 's' | 'm';
  /** Theme */
  theme?: Theme;
}

export const Divider = memo(
  ({
    height = 's',
    isHorizontal = true,
    label,
    theme,
    width,
    flex = !isHorizontal || width ? 'none' : 'auto',
  }: Props) => {
    const isDarkTheme = theme === 'dark';
    return (
      <Box
        role="separator"
        flex={flex}
        styles={componentStyles.divider}
        styleProps={{
          height,
          isDarkTheme,
          isHorizontal,
          width,
        }}
      >
        {label && isHorizontal && (
          <Text
            p={1}
            color={isDarkTheme ? 'inverse.text.primary' : 'text.primary'}
            styles={componentStyles.label}
            styleProps={{
              isDarkTheme,
            }}
            variant="tiny-bold"
          >
            {label}
          </Text>
        )}
      </Box>
    );
  },
);

const componentStyles = {
  divider: ({
    height,
    isDarkTheme,
    isHorizontal,
    width,
  }: {
    height?: 's';
    isDarkTheme?: boolean;
    isHorizontal?: boolean;
    width?: 's' | 'm';
  }) => ({
    borderColor: isDarkTheme ? 'inverse.border.divider' : 'border',
    position: 'relative',
    ...(isHorizontal
      ? {
          borderBottom: isDarkTheme ? 'inverse.border.divider' : 'border',
          height: '1px',
          width: width ? `divider.width.${width}` : '100%',
        }
      : {
          borderLeft: isDarkTheme ? 'inverse.border.divider' : 'border',
          height: height ? `divider.height.${height}` : '100%',
          width: '1px',
        }),
  }),
  label: ({ isDarkTheme }: { isDarkTheme?: boolean }) => ({
    backgroundColor: isDarkTheme
      ? 'inverse.background.secondary'
      : 'background.secondary',
    borderRadius: 'm',
    color: isDarkTheme ? 'inverse.text.primary' : 'text.primary',
    left: '50%',
    position: 'absolute',
    top: '50%',
    textTransform: 'uppercase',
    transform: 'translate(-50%, -50%)',
  }),
};
